/**
 * BuildProgressCard — Pinned progress card shown on Home during first build
 *
 * Shows source-type-aware messaging, progress bar, and the current
 * pipeline phase from backend SSE events (via friendlyBuildMessage).
 */

import React from 'react';
import {
  Text,
  Button,
  ProgressBar,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useDataModels } from '../../contexts/DataModelsContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useActiveJobProgress } from '../../contexts/WorkspaceEventsContext';
import { translateBuildPhase } from '../../utils/buildPhaseMessages';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('20px', '24px'),
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', tokens.colorBrandStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow2,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  title: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '18px',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  progressBar: {
    flex: 1,
  },
  progressPct: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    minWidth: '36px',
    textAlign: 'right' as const,
  },
  activeStage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSubtitle(connectorId: string | null): string {
  switch (connectorId) {
    case 'salesforce':
      return 'AI is syncing CRM data and discovering relationships. Large orgs can take 30–60 minutes.';
    case 'simulator':
      return 'AI is generating demo CRM data and preparing a test workspace.';
    case 'excel':
    case 'csv':
      return 'AI is profiling your file and creating a semantic model.';
    default:
      return 'AI is analyzing your data and building a semantic model.';
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

interface BuildProgressCardProps {
  connectorId: string | null;
}

const BuildProgressCard: React.FC<BuildProgressCardProps> = ({ connectorId }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { dataModels } = useDataModels();
  const { connections } = useConnections();

  const activeJob = useActiveJobProgress();
  const progress = activeJob?.progress ?? 0;
  const currentMessage = translateBuildPhase(activeJob?.messageId, activeJob?.message);

  const handleViewProgress = () => {
    const activeSource = connections.find(c =>
      ['syncing', 'draft', 'authenticating', 'configuring'].includes(c.status)
    );
    if (activeSource) {
      navigate(`/data-sources/${activeSource.id}`);
    } else {
      const buildingModel = dataModels.find(m => m.status === 'building');
      if (buildingModel) {
        navigate(`/data-models/${buildingModel.id}`);
      }
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Text className={styles.title}>Building your analytics workspace</Text>
          <Text className={styles.subtitle}>{getSubtitle(connectorId)}</Text>
        </div>
      </div>

      {/* Progress bar */}
      <div className={styles.progressRow}>
        <ProgressBar
          value={progress / 100}
          thickness="large"
          className={styles.progressBar}
        />
        <Text className={styles.progressPct}>{Math.round(progress)}%</Text>
      </div>

      {/* Current stage from real SSE events */}
      <div className={styles.activeStage}>
        <Spinner size="extra-tiny" />
        <Text>{currentMessage}</Text>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button appearance="outline" size="small" onClick={handleViewProgress}>
          View progress
        </Button>
        {connections.some(c => c.status === 'ready') && (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => navigate('/data-sources')}
          >
            Browse imported data
          </Button>
        )}
      </div>
    </div>
  );
};

export default BuildProgressCard;